import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ViewChildren,
  ElementRef,
  Input,
  AfterContentInit,
  ContentChild,
  ViewChild,
  QueryList,
  HostListener,
} from "@angular/core";

import { ArgTypes } from "@storybook/angular/types-6-0";
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from "@vattenfall/util";

import { InputSublabelDirective } from "../lists";
import { ListsElementAccessor } from "../lists-accessor";

export const argTypesSelect: ArgTypes = <const>{
  sublabel: {
    control: "text",
    description: "Sublabel for input element. Behaves the same as `<ui-input-sublabel>` element.",
    defaultValue: "Input sublabel",
    table: {
      category: "Content",
      type: {
        summary: "string | null",
      },
    },
  },
  relative: {
    control: "boolean",
    description:
      "Whether the select should expand over surrounding elements, or if the surrounding elements should expand with it.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  truncate: {
    control: "boolean",
    description: "Whether the select text content should break into rows or be truncated.",
    defaultValue: true,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  determinate: {
    control: "boolean",
    description: "Whether the control is allowed to be empty after dirty.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  fluid: {
    control: "boolean",
    description: "Whether the element width should be fluid or fixed.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  size: {
    control: "number",
    description: "The number of visible items in the control.",
    defaultValue: null,
    table: {
      category: "Inputs",
      type: {
        summary: "number | null",
      },
    },
  },
};

/**
 * Select menus display a list of choices on temporary surfaces.
 *
 * Use select menus to:
 *
 * * Provide a list of related options
 * * Where the options are plenty
 *
 * Select menus support the same keyboard interaction as native `<select>` elements.
 *
 * Select menus can be used as an Angular `FormControl`, in both reactive and template driven forms.
 */
@Component({
  selector: "ui-input[type=select]",
  templateUrl: "./select.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSelectComponent extends ListsElementAccessor implements AfterContentInit {
  @ViewChild("detailsElement", { read: ElementRef }) private detailsElement!: ElementRef<HTMLDetailsElement>;

  /**
   * Close select when clicked outside of its content area.
   */
  @HostListener("document:click", ["$event"]) onClick($event: PointerEvent) {
    if (!$event.composedPath().includes(this.detailsElement.nativeElement)) {
      this.open = false;
    }
  }

  /**
   * Sublabel for input element. Behaves the same as `<ui-input-sublabel>` element.
   */
  @Input() sublabel: string | null = null;
  /**
   * Whether the select should expand over surrounding elements, or if the surrounding elements should expand with it.
   *
   * Default is `false`.
   */
  @Input()
  get relative(): boolean {
    return this._relative;
  }
  set relative(value: BooleanInput) {
    this._relative = coerceBooleanProperty(value);
  }
  private _relative = false;
  /**
   * Whether the select text content should break into rows or be truncated.
   *
   * Default is `true`.
   */
  @Input()
  get truncate(): boolean {
    return this._truncate;
  }
  set truncate(value: BooleanInput) {
    this._truncate = coerceBooleanProperty(value);
  }
  private _truncate = true;
  /**
   * Whether the control is allowed to be empty after dirty.
   *
   * Default is `false`.
   */
  @Input()
  get determinate(): boolean {
    return this._determinate;
  }
  set determinate(value: BooleanInput) {
    this._determinate = coerceBooleanProperty(value);
  }
  private _determinate = false;
  /**
   * Whether the element width should be fluid or fixed.
   *
   * Default is `false`.
   */
  @Input()
  get fluid(): boolean {
    return this._fluid;
  }
  set fluid(value: BooleanInput) {
    this._fluid = coerceBooleanProperty(value);
  }
  private _fluid = false;
  /**
   * The number of visible items in the control.
   *
   * Default is `null`.
   */
  @Input()
  get size(): NumberInput {
    return this._size;
  }
  set size(value: NumberInput) {
    if (value != null) {
      this._size = coerceNumberProperty(value);
      // Size attribute should only be applied on multiple select elements
      if (this.multiple) this.setProperty("size", this._size);
    }
  }
  private _size: NumberInput = null;

  // Keep tracks of the select open and close state
  open = false;

  // Maximum height of select element when expanded
  maxHeight = "auto";

  // Whether the input has been opened or not
  hasOpened = false;

  override ngAfterContentInit() {
    super.ngAfterContentInit();
    if (this.inputSublabelDirective) this.sublabel = this.inputSublabelDirective?.elementRef.nativeElement.textContent;
    // Placing last in callstack to ensure the *ngFor loop has completed
    setTimeout(() => {
      if (this.size && this.size < this.inputOptionDirectives.length)
        this.maxHeight =
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.labelElements!.reduce((maxHeight, labelElement, index) => {
            if (index < Number(this.size) + 1) maxHeight += labelElement.nativeElement.offsetHeight;
            return maxHeight;
          }, 0) -
          24 +
          "px";
    });
  }

  getSelectedTitle() {
    return this.selectedLabels.map((selectedLabel) => selectedLabel.label).join(", ");
  }

  /**
   * Callback function invoked when the select is opened or closed.
   *
   * When the selected is opened, this will set the focus on the first selected option
   * or the first option in the list if none has been selected.
   */
  onToggle(event: Event) {
    // Do nothing if empty
    if (!this.inputOptionDirectives.length) return;
    // Override browser default open event, we will set the open state manually
    event.preventDefault();
    if (this.isEnabled) {
      if (!this.open) {
        const firstInputElementChecked = this.inputOptionElements.find(
          (inputOptionElement) => inputOptionElement.nativeElement.checked
        )?.nativeElement;
        const firstInputElement = this.inputOptionElements.first.nativeElement;
        const inputElement = firstInputElementChecked ?? firstInputElement;
        // Putting this at the end of callstack to ensure the options
        // has been rendered in the ngFor loop first
        setTimeout(() => inputElement.focus());
        this.hasOpened = true;
      }
      this.open = !this.open;
    }
  }
  /**
   * Callback function invoked when navigating the select using keyboard.
   *
   * Enables keyboard navigation that behaves similarly as a native select element.
   */
  onNavigate(event: KeyboardEvent) {
    // Prevent default keyboard events
    event.preventDefault();
    // Prevent event propagation in details element
    event.stopImmediatePropagation();
    const inputElement = event.target as HTMLInputElement;
    const labelElement = inputElement.parentNode as HTMLLabelElement;
    const previousElementSibling = labelElement.previousElementSibling?.querySelector("input") as HTMLInputElement | null;
    const nextElementSibling = labelElement.nextElementSibling?.querySelector("input") as HTMLInputElement | null;
    const firstElementChild = labelElement.parentNode?.firstElementChild?.querySelector("input") as HTMLInputElement | null;
    const lastElementChild = labelElement.parentNode?.lastElementChild?.querySelector("input") as HTMLInputElement | null;
    // Switch between navigation key to focus the next or previous element in list
    switch (event.code) {
      // Go to previous option or the last option if the current option is the first in the list
      case "ArrowUp": {
        inputElement.tabIndex = -1;
        if (previousElementSibling) {
          previousElementSibling.tabIndex = 0;
          previousElementSibling.focus();
        } else if (lastElementChild) {
          lastElementChild.tabIndex = 0;
          lastElementChild.focus();
        }
        break;
      }
      // Go to next option or the first option if the current option is the last in the list
      case "ArrowDown": {
        inputElement.tabIndex = -1;
        if (nextElementSibling) {
          nextElementSibling.tabIndex = 0;
          nextElementSibling.focus();
        } else if (firstElementChild) {
          firstElementChild.tabIndex = 0;
          firstElementChild.focus();
        }
        break;
      }
      // Close the dropdown on escape and reset focus
      case "Escape": {
        inputElement.tabIndex = -1;
        if (firstElementChild) {
          firstElementChild.tabIndex = 0;
        }
        this.detailsElement.nativeElement.querySelector("summary")?.focus();
        this.open = false;
        break;
      }
      // Selected the focused option
      case "Enter":
      case "Space": {
        inputElement.tabIndex = -1;
        if (firstElementChild) {
          firstElementChild.tabIndex = 0;
        }
        this.onSelected(event, inputElement);
        break;
      }
    }
  }

  onBlur() {
    if (!this.hasOpened) super.onTouched();
  }

  /**
   * Callback function invoked when an option has been selected.
   *
   * Close the select element and resets focus. Emit select change event.
   */
  onSelected(event: Event, inputElement: HTMLInputElement) {
    // Prevent default click event
    event.preventDefault();
    // Prevent event propagation in details element
    event.stopImmediatePropagation();
    super.onTouched();
    // Do not collapse options list when a value has been selected
    if (!this.multiple) {
      const hasSelectionRejected =
        !this.determinate &&
        inputElement.checked &&
        this.inputOptionElements.filter((inputOptionElement) => inputOptionElement.nativeElement.checked).length === 1;
      // Do not deselect a determinate select if there is only one selected value remaining
      if (!hasSelectionRejected) {
        inputElement.checked = !inputElement.checked;
        this.detailsElement.nativeElement.querySelector("summary")?.focus();
        this.open = false;
        super.onSelectChange();
      }
    } else {
      inputElement.checked = !inputElement.checked;
      super.onSelectChange();
    }
  }

  @ViewChildren("labelElement", { read: ElementRef }) protected labelElements?: QueryList<ElementRef<HTMLLabelElement>>;
  @ContentChild(InputSublabelDirective) protected inputSublabelDirective?: InputSublabelDirective;
}
